import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase-config';
import { Button } from '../components/ui/button';
import { Loader2, Building2, LogOut } from 'lucide-react';

interface CustomerAccess {
  id: string;
  slug: string;
  companyName: string;
  role: 'super_admin' | 'customer_admin' | 'user';
  brandColor: string;
  logoUrl?: string;
}

export default function DashboardPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<CustomerAccess[]>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    determineRedirect();
  }, [user]);

  const determineRedirect = async () => {
    if (!user) return;

    try {
      console.log('🔍 Determining redirect for user:', user.email);

      // 1. Check if user is a super admin
      // Note: This will throw permission-denied if user is not a super admin, which is expected
      let isSuperAdmin = false;
      try {
        const superAdminDoc = await getDoc(doc(db, 'admins', user.uid));
        isSuperAdmin = superAdminDoc.exists();
        if (isSuperAdmin) {
          console.log('👑 User is super admin, redirecting to /admin');
          navigate('/admin', { replace: true });
          return;
        }
      } catch (superAdminError: any) {
        // Permission denied is expected for non-super-admins, continue to customer admin check
        if (superAdminError.code !== 'permission-denied') {
          console.error('❌ Unexpected error checking super admin:', superAdminError);
        }
        console.log('ℹ️ User is not super admin, checking customer admin access');
      }

      // 2. Get all customers
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customerAccessList: CustomerAccess[] = [];

      // 3. Check each customer to see if user has access
      for (const customerDoc of customersSnapshot.docs) {
        const customerData = customerDoc.data();
        const customerId = customerDoc.id;

        // Check if user is a customer admin
        const adminDoc = await getDoc(doc(db, 'customers', customerId, 'admins', user.uid));
        if (adminDoc.exists()) {
          customerAccessList.push({
            id: customerId,
            slug: customerData.slug,
            companyName: customerData.companyName,
            role: 'customer_admin',
            brandColor: customerData.brandColor || '#6366f1',
            logoUrl: customerData.logoUrl,
          });
        }
      }

      // 4. If user is a customer admin
      if (customerAccessList.length > 0) {
        if (customerAccessList.length === 1) {
          // Single customer admin - redirect directly
          const customer = customerAccessList[0];
          console.log('🏢 User is admin for one company, redirecting to:', `/${customer.slug}/admin`);
          navigate(`/${customer.slug}/admin`, { replace: true });
          return;
        } else {
          // Multiple customer admin access - show selection
          console.log('🏢 User is admin for multiple companies, showing selection');
          setCompanies(customerAccessList);
          setLoading(false);
          return;
        }
      }

      // 5. User has no admin access - they're a regular user
      // Show empty state instead of redirecting to avoid loops
      console.log('👤 User has no admin access, showing empty state');
      setCompanies([]);
      setLoading(false);
    } catch (error) {
      console.error('Error determining redirect:', error);
      setCompanies([]);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Multiple companies - show selection
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">ProductWorkshop</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                {user?.email}
              </div>
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center space-y-6 mb-12">
          <h2 className="text-3xl font-bold text-foreground">
            Select a Company
          </h2>
          <p className="text-muted-foreground">
            You have admin access to multiple companies. Choose one to manage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {companies.map((company) => (
            <button
              key={company.id}
              onClick={() => navigate(`/${company.slug}/admin`)}
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary transition-all hover:shadow-lg text-left"
            >
              <div className="flex items-center gap-4">
                {company.logoUrl ? (
                  <div className="w-16 h-16 rounded border border-border flex items-center justify-center bg-white overflow-hidden flex-shrink-0">
                    <img
                      src={company.logoUrl}
                      alt={`${company.companyName} logo`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div
                    className="w-16 h-16 rounded flex items-center justify-center border border-border flex-shrink-0"
                    style={{ backgroundColor: company.brandColor }}
                  >
                    <Building2 className="h-8 w-8 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                    {company.companyName}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Admin Portal
                  </p>
                </div>
                <div className="text-muted-foreground group-hover:text-primary transition-colors">
                  →
                </div>
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
